import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { MdAdd, MdEdit, MdLock, MdVisibility } from "react-icons/md";
import { getPredictions, savePrediction } from "../services/predictionsService";
import { brandInputClass } from "../components/form/fieldStyles";
import { BrandButton } from "../components/ui/BrandButton";
import { EmojiSymbol } from "../components/ui/EmojiSymbol";
import { useAppLoading } from "../context/AppLoadingContext";
import { useTrackedImagePreload } from "../hooks/useTrackedImagePreload";
import { getTeamDisplayName, getTeamFlagEmoji } from "../utils/flagEmoji";
import predictionModalBackground from "../assets/brand/backgrounds/DG1.webp";

const MAX_PREDICTION_GOALS = 20;

function formatKickoff(dateValue) {
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "Fecha por definir";
  }

  return new Intl.DateTimeFormat("es-UY", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatGroupName(groupName) {
  if (!groupName) {
    return "";
  }

  const normalized = String(groupName).trim().toUpperCase().replace(/\s+/g, "_");

  if (!normalized.startsWith("GROUP_")) {
    return groupName;
  }

  const suffix = normalized.slice("GROUP_".length);
  return `Grupo ${suffix}`;
}

function getPhaseDisplayName(phaseName) {
  const normalized = String(phaseName ?? "")
    .trim()
    .toLowerCase();

  if (!normalized) {
    return "Mundial";
  }

  if (normalized.includes("group")) {
    return "Fase de grupos";
  }

  if (normalized.includes("round of 32") || normalized.includes("last 32")) {
    return "Dieciseisavos de final";
  }

  if (normalized.includes("round of 16") || normalized.includes("last 16")) {
    return "Octavos de final";
  }

  if (normalized.includes("quarter")) {
    return "Cuartos de final";
  }

  if (normalized.includes("semi")) {
    return "Semifinales";
  }

  if (normalized.includes("third")) {
    return "Partido por el tercer puesto";
  }

  if (normalized.includes("final")) {
    return "Final";
  }

  return phaseName;
}

function getFixtureContextLabel(fixture) {
  if (fixture.groupName) {
    return formatGroupName(fixture.groupName);
  }

  const roundName = String(fixture.apiRoundName ?? fixture.phaseName ?? "").toLowerCase();

  if (roundName.includes("semi")) {
    return "Semifinales";
  }

  if (roundName.includes("quarter")) {
    return "Cuartos de final";
  }

  if (roundName.includes("third")) {
    return "Partido por el tercer puesto";
  }

  if (roundName.includes("final")) {
    return "Final";
  }

  return getPhaseDisplayName(fixture.phaseName ?? fixture.apiRoundName ?? "Partido");
}

function isPlaceholderTeamName(teamName) {
  const normalized = String(teamName ?? "").trim().toLowerCase();

  if (!normalized) {
    return true;
  }

  return [
    "tbd",
    "to be determined",
    "winner",
    "winner of",
    "loser",
    "loser of",
    "pending",
    "pendiente",
    "pendiente de definir",
    "to be announced",
    "unknown",
    "undefined",
  ].some((fragment) => normalized.includes(fragment));
}

function hasRenderableFixtureTeams(fixture) {
  return (
    fixture &&
    !isPlaceholderTeamName(fixture.teams?.home?.name) &&
    !isPlaceholderTeamName(fixture.teams?.away?.name)
  );
}

function getRenderablePhases(phases) {
  return (phases ?? [])
    .map((phase) => ({
      ...phase,
      displayName: getPhaseDisplayName(phase.name),
      fixtures: (phase.fixtures ?? []).filter(hasRenderableFixtureTeams),
    }))
    .filter((phase) => phase.fixtures.length > 0)
    .sort((left, right) => {
      const sortOrderDifference = (right.sortOrder ?? 0) - (left.sortOrder ?? 0);

      if (sortOrderDifference !== 0) {
        return sortOrderDifference;
      }

      return String(left.displayName).localeCompare(String(right.displayName));
      });
}

function filterPhasesByFixturePredicate(phases, predicate) {
  return (phases ?? [])
    .map((phase) => ({
      ...phase,
      fixtures: (phase.fixtures ?? []).filter(predicate),
    }))
    .filter((phase) => phase.fixtures.length > 0);
}

function getRenderableFixtures(phases) {
  return phases.flatMap((phase) => phase.fixtures.map((fixture) => ({
    ...fixture,
    phaseName: phase.displayName,
    phaseSortOrder: phase.sortOrder,
  })));
}

function getNextFixtureId(orderedFixtures, currentFixtureId) {
  const currentIndex = orderedFixtures.findIndex((fixture) => fixture.id === currentFixtureId);

  if (currentIndex < 0) {
    return null;
  }

  return orderedFixtures[currentIndex + 1]?.id ?? null;
}

function renderTeamLabel(teamName) {
  const flagEmoji = getTeamFlagEmoji(teamName);
  const displayName = getTeamDisplayName(teamName) || "Pendiente de definir";

  return (
    <span className="inline-flex items-center gap-2">
      {flagEmoji ? (
        <EmojiSymbol emoji={flagEmoji} className="text-[1em] leading-none" />
      ) : null}
      <span className="min-w-0 whitespace-normal break-words">{displayName}</span>
    </span>
  );
}

function renderScorePair(homeValue, awayValue) {
  const hasHome = homeValue !== null && homeValue !== undefined;
  const hasAway = awayValue !== null && awayValue !== undefined;

  if (!hasHome && !hasAway) {
    return "-";
  }

  return `${hasHome ? homeValue : "-"} - ${hasAway ? awayValue : "-"}`;
}

function getRenderableRegularTimeScore(fixture) {
  return {
    home:
      fixture?.score?.regularTime?.home ??
      fixture?.score?.fullTime?.home ??
      null,
    away:
      fixture?.score?.regularTime?.away ??
      fixture?.score?.fullTime?.away ??
      null,
  };
}

function isLiveFixtureStatus(statusShort) {
  const normalized = String(statusShort ?? "").trim().toUpperCase();

  return ["IN_PLAY", "PAUSED", "EXTRA_TIME", "SUSPENDED"].includes(normalized);
}

function getLiveMinuteLabel(fixture) {
  if (!isLiveFixtureStatus(fixture?.status?.short)) {
    return "";
  }

  const elapsed = Number(fixture?.status?.elapsed);

  if (!Number.isFinite(elapsed) || elapsed <= 0) {
    return "";
  }

  return `${Math.trunc(elapsed)}'`;
}

function getCompactMatchLabel(homeTeamName, awayTeamName) {
  const homeFlagEmoji = getTeamFlagEmoji(homeTeamName) || "🏳️";
  const awayFlagEmoji = getTeamFlagEmoji(awayTeamName) || "🏳️";

  return (
    <span className="flex w-full min-w-0 flex-nowrap items-center justify-center gap-1.5 overflow-hidden sm:gap-2">
      <span className="inline-flex min-w-0 items-center gap-1.5 sm:gap-2">
        <EmojiSymbol emoji={homeFlagEmoji} className="text-[1em] leading-none" />
        <span className="min-w-0 whitespace-nowrap text-[0.74rem] font-semibold leading-none sm:text-[0.95rem]">
          {getTeamDisplayName(homeTeamName)}
        </span>
      </span>
      <span className="shrink-0 text-slate-400">vs</span>
      <span className="inline-flex min-w-0 items-center gap-1.5 sm:gap-2">
        <span className="min-w-0 whitespace-nowrap text-[0.74rem] font-semibold leading-none sm:text-[0.95rem]">
          {getTeamDisplayName(awayTeamName)}
        </span>
        <EmojiSymbol emoji={awayFlagEmoji} className="text-[1em] leading-none" />
      </span>
    </span>
  );
}

function ChevronIcon({ className = "" }) {
  return (
    <svg
      className={`h-4 w-4 transition-transform ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 9l6 6 6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function validatePredictionGoals(rawValue, label) {
  if (rawValue === "") {
    return `Completá ${label.toLowerCase()} antes de guardar.`;
  }

  const parsedValue = Number(rawValue);

  if (!Number.isInteger(parsedValue)) {
    return `La predicción ${label.toLowerCase()} debe ser un número entero.`;
  }

  if (parsedValue < 0) {
    return `La predicción ${label.toLowerCase()} no puede ser negativa.`;
  }

  if (parsedValue > MAX_PREDICTION_GOALS) {
    return `Se puede poner hasta ${MAX_PREDICTION_GOALS} goles.`;
  }

  return null;
}

function getPredictionDraftError(draft) {
  if (!draft) {
    return "";
  }

  const homeGoals = Number(draft.homeGoals);
  const awayGoals = Number(draft.awayGoals);

  if (Number.isFinite(homeGoals) && homeGoals > MAX_PREDICTION_GOALS) {
    return `Se puede poner hasta ${MAX_PREDICTION_GOALS} goles.`;
  }

  if (Number.isFinite(awayGoals) && awayGoals > MAX_PREDICTION_GOALS) {
    return `Se puede poner hasta ${MAX_PREDICTION_GOALS} goles.`;
  }

  return "";
}

function formatPercent(value) {
  if (!Number.isFinite(value)) {
    return "0%";
  }

  return `${Math.round(value)}%`;
}

function getOutcomeBadgeColor(outcome) {
  if (outcome === "HOME_WIN") {
    return "#325FDC";
  }

  if (outcome === "AWAY_WIN") {
    return "#91BEFA";
  }

  return "#F0F0EB";
}

function getOutcomeLabel(outcome, homeTeamName, awayTeamName) {
  if (outcome === "HOME_WIN") {
    return `Gana ${getTeamDisplayName(homeTeamName)}`;
  }

  if (outcome === "AWAY_WIN") {
    return `Gana ${getTeamDisplayName(awayTeamName)}`;
  }

  return "Empate";
}

function getDisplayOutcomeLabel(label) {
  const resolvedLabel = String(label ?? "").trim();

  if (!resolvedLabel) {
    return "";
  }

  if (resolvedLabel.toLowerCase() === "empate" || resolvedLabel.toLowerCase() === "draw") {
    return "Empate";
  }

  const winMatch = resolvedLabel.match(/^Gana\s+(.+)$/i);

  if (winMatch) {
    return `Gana ${getTeamDisplayName(winMatch[1])}`;
  }

  return resolvedLabel;
}

function getEvaluationLabel(resultCode) {
  switch (resultCode) {
    case "EXACT_SCORE":
      return "Resultado exacto";
    case "CORRECT_OUTCOME_AND_GOAL_DIFFERENCE":
      return "Resultado y diferencia acertados";
    case "CORRECT_OUTCOME":
      return "Ganador o empate acertado";
    case "INCORRECT":
      return "Sin puntaje";
    default:
      return "Evaluado";
  }
}

function isFixtureEditableNow(fixture, currentTime) {
  const kickoffAt = new Date(fixture.kickoffAt);

  if (Number.isNaN(kickoffAt.getTime())) {
    return false;
  }

  return currentTime < kickoffAt.getTime();
}

function getDraftFixtureValues(fixture) {
  return {
    homeGoals:
      fixture.prediction?.predictedHomeGoals === null ||
      fixture.prediction?.predictedHomeGoals === undefined
        ? ""
        : String(fixture.prediction.predictedHomeGoals),
    awayGoals:
      fixture.prediction?.predictedAwayGoals === null ||
      fixture.prediction?.predictedAwayGoals === undefined
        ? ""
        : String(fixture.prediction.predictedAwayGoals),
  };
}

function buildDraftMap(tournamentData) {
  const drafts = {};

  for (const phase of tournamentData?.phases ?? []) {
    for (const fixture of phase.fixtures ?? []) {
      drafts[fixture.id] = getDraftFixtureValues(fixture);
    }
  }

  return drafts;
}

function getFixtureEditability(fixture, currentTime) {
  return isFixtureEditableNow(fixture, currentTime);
}

function updateFixtureInTournament(tournamentData, fixtureId, updater) {
  if (!tournamentData) {
    return tournamentData;
  }

  return {
    ...tournamentData,
    phases: tournamentData.phases.map((phase) => ({
      ...phase,
      fixtures: phase.fixtures.map((fixture) =>
        fixture.id === fixtureId ? updater(fixture) : fixture,
      ),
    })),
  };
}

function PredictionSection({ title, isCollapsed, onToggle, children }) {
  return (
    <section className="grid gap-3 overflow-x-hidden rounded-[1.75rem] border border-slate-200 bg-white p-3 shadow-sm sm:gap-4 sm:p-5">
      <button
        type="button"
        onClick={onToggle}
        className="flex min-h-[4.25rem] w-full items-center justify-between gap-3 overflow-hidden rounded-[1.35rem] text-left"
        aria-expanded={!isCollapsed}
      >
        <h3 className="min-w-0 flex-1 font-display text-[1.25rem] leading-tight text-brand-primary sm:text-[2rem]">
          {title}
        </h3>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-brand-primary">
          <ChevronIcon className={isCollapsed ? "" : "rotate-180"} />
        </span>
      </button>

      {isCollapsed ? null : <div className="grid gap-6">{children}</div>}
    </section>
  );
}

function renderFixtureCard(fixture, { handleOpenFixture, currentTime }) {
  const prediction = fixture.prediction;
  const isEditable = getFixtureEditability(fixture, currentTime);
  const showActionButton = isEditable;
  const isCompactLabel = getCompactMatchLabel(fixture.teams.home.name, fixture.teams.away.name);
  const actualScore = getRenderableRegularTimeScore(fixture);
  const liveMinuteLabel = getLiveMinuteLabel(fixture);
  const isLiveNow = Boolean(liveMinuteLabel);
  const resultLabel = liveMinuteLabel ? "Resultado parcial" : "Resultado real";
  const resultScoreClassName = isLiveNow
    ? "text-brand-secondary"
    : "text-slate-400";
  const actionLabel = prediction ? (isEditable ? "Editar" : "Ver") : isEditable ? "Cargar" : "Cerrado";
  const isActionDisabled = !prediction && !isEditable;
  const ActionIcon = prediction ? (isEditable ? MdEdit : MdVisibility) : isEditable ? MdAdd : MdLock;
  const actionVariant = prediction ? (isEditable ? "secondary" : "outline") : isEditable ? "primary" : "outline";
  const actionButtonClassName = [
    "h-8 w-8 shrink-0 border shadow-sm sm:h-9 sm:w-9",
    prediction
      ? isEditable
        ? "border-brand-primary/20 bg-brand-primary text-white hover:bg-brand-secondary"
        : "border-brand-primary/20 bg-white text-brand-primary hover:bg-brand-primary/5"
      : isEditable
        ? "border-brand-gold-soft/70 bg-brand-gold-soft text-brand-primary hover:brightness-105"
        : "border-slate-300 bg-slate-100 text-slate-400",
  ]
    .join(" ");

  return (
    <div
      key={fixture.id}
      className={`grid h-full gap-2 rounded-[1.15rem] border border-slate-200 bg-slate-50 p-2 lg:items-center lg:gap-4 lg:p-3 ${
        showActionButton
          ? "lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto]"
          : "lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]"
      }`}
    >
      <div className="lg:hidden">
        <p className="text-center font-display text-[0.88rem] font-semibold leading-tight text-slate-900 sm:text-lg">
          {isCompactLabel}
        </p>
      </div>

      <div className="hidden min-h-[1.75rem] min-w-0 items-center lg:flex">
        <p className="text-lg font-semibold leading-tight text-slate-900">
          {renderTeamLabel(fixture.teams.home.name)}
        </p>
      </div>

      <div className="relative justify-self-center rounded-[1rem] border border-brand-primary/10 bg-white px-3 py-2 text-center shadow-sm lg:w-full lg:max-w-[12rem]">
        <div className="grid gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="min-w-0">
              <p className="text-[0.5rem] font-semibold uppercase tracking-[0.24em] text-slate-400 sm:text-[0.55rem]">
                Tu predicción
              </p>
              <p className="mt-0.5 inline-flex items-baseline gap-1 whitespace-nowrap font-display text-xl leading-none text-brand-primary lg:text-[1.9rem]">
                <span>{renderScorePair(prediction?.predictedHomeGoals, prediction?.predictedAwayGoals)}</span>
              </p>
            </div>

            <div className="min-w-0">
              <p className="text-[0.5rem] font-semibold uppercase tracking-[0.24em] text-slate-400 sm:text-[0.55rem]">
                {resultLabel}
              </p>
              <p className={`mt-0.5 inline-flex items-baseline gap-1 whitespace-nowrap font-display text-xl leading-none lg:text-[1.9rem] ${resultScoreClassName}`}>
                <span>{renderScorePair(actualScore.home, actualScore.away)}</span>
              </p>
            </div>
          </div>

          <p className="text-[0.68rem] text-slate-500">{formatKickoff(fixture.kickoffAt)}</p>

          {fixture.evaluation ? (
            <p className="text-[0.62rem] font-medium text-brand-primary sm:text-[0.68rem]">
              {getEvaluationLabel(fixture.evaluation.resultCode)} · {fixture.evaluation.points} pts
            </p>
          ) : null}
        </div>
      {showActionButton ? (
        <div className="absolute bottom-2 right-2 z-10 lg:hidden">
          <BrandButton
            type="button"
            onClick={() => handleOpenFixture(fixture)}
            variant={actionVariant}
            radius="lg"
            size="icon"
            motion="subtle"
            className={`${actionButtonClassName} rounded-[0.7rem]`}
            disabled={isActionDisabled}
            aria-label={actionLabel}
            title={actionLabel}
          >
            <ActionIcon aria-hidden="true" className="h-4 w-4" />
            <span className="sr-only">{actionLabel}</span>
          </BrandButton>
        </div>
      ) : null}
      </div>

      <div className="hidden min-h-[1.75rem] min-w-0 items-center justify-end lg:flex">
        <p className="text-lg font-semibold leading-tight text-slate-900 lg:text-right">
          {renderTeamLabel(fixture.teams.away.name)}
        </p>
      </div>

      {showActionButton ? (
        <div className="hidden justify-end lg:flex lg:justify-self-end">
          <BrandButton
            type="button"
            onClick={() => handleOpenFixture(fixture)}
            variant={actionVariant}
            radius="lg"
            size="icon"
            motion="subtle"
            className={`${actionButtonClassName} rounded-[0.7rem]`}
            disabled={isActionDisabled}
            aria-label={actionLabel}
            title={actionLabel}
          >
            <ActionIcon aria-hidden="true" className="h-4 w-4" />
            <span className="sr-only">{actionLabel}</span>
          </BrandButton>
        </div>
      ) : null}
    </div>
  );
}

function PredictionModal({
  fixture,
  draft,
  isEditable,
  isSaving,
  errorMessage,
  onClose,
  onChangeHomeGoals,
  onChangeAwayGoals,
  onSave,
  onSaveAndNext,
}) {
  const communityStats = fixture.communityStats ?? {
    totalVotes: 0,
    outcomeBreakdown: [],
    topScorelines: [],
    leadingOutcome: null,
  };
  const leadingOutcome = communityStats.leadingOutcome;
  const leadingOutcomeLabel = getDisplayOutcomeLabel(leadingOutcome?.label) || "Todavía no hay votos";
  const leadingOutcomePercentage = leadingOutcome?.percentage ?? 0;
  const leadingOutcomeColor = getOutcomeBadgeColor(leadingOutcome?.outcome);
  const compactFixtureLabel = getCompactMatchLabel(fixture.teams.home.name, fixture.teams.away.name);
  const topScorelineCount = communityStats.topScorelines.length;
  const topScorelineCardMinHeight = topScorelineCount <= 1 ? "6.25rem" : topScorelineCount === 2 ? "5.9rem" : "5.45rem";
  const topScorelineCardMinHeightSm =
    topScorelineCount <= 1 ? "8.5rem" : topScorelineCount === 2 ? "8rem" : "7.25rem";
  const topScorelineCircleSize = `calc(${topScorelineCardMinHeight} - 1.15rem)`;
  const topScorelineCircleSizeSm = `calc(${topScorelineCardMinHeightSm} - 1.5rem)`;
  const topScorelineCircleInset = topScorelineCount <= 1 ? "0.26rem" : topScorelineCount === 2 ? "0.23rem" : "0.2rem";
  const topScorelineCircleInsetSm = topScorelineCount <= 1 ? "0.38rem" : topScorelineCount === 2 ? "0.35rem" : "0.3rem";
  const topScorelinePercentSize = topScorelineCount <= 1 ? "1rem" : topScorelineCount === 2 ? "0.9rem" : "0.82rem";
  const topScorelinePercentSizeSm = topScorelineCount <= 1 ? "1.32rem" : topScorelineCount === 2 ? "1.2rem" : "1.05rem";
  const topScorelineScoreSize = topScorelineCount <= 1 ? "1.08rem" : topScorelineCount === 2 ? "0.98rem" : "0.9rem";
  const topScorelineScoreSizeSm = topScorelineCount <= 1 ? "1.45rem" : topScorelineCount === 2 ? "1.3rem" : "1.16rem";
  const topScorelineLabelSize = topScorelineCount <= 1 ? "0.72rem" : topScorelineCount === 2 ? "0.66rem" : "0.62rem";
  const topScorelineLabelSizeSm = topScorelineCount <= 1 ? "0.92rem" : topScorelineCount === 2 ? "0.82rem" : "0.74rem";

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 px-4 py-4 backdrop-blur-md"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="relative h-[calc(100dvh-1.25rem)] w-full max-w-5xl overflow-hidden rounded-[2rem] border border-white/35 shadow-[0_30px_80px_rgba(15,23,42,0.32)] md:h-[calc(100vh-2rem)] md:max-h-[calc(100vh-2rem)]"
        role="dialog"
        aria-modal="true"
        aria-label="Cargar predicción"
        style={{
          backgroundColor: "#DCE56D",
          backgroundImage: `linear-gradient(135deg, rgba(170,205,105,0.82), rgba(255,255,255,0.18)), url(${predictionModalBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(255,255,255,0.18),_transparent_40%),linear-gradient(180deg,rgba(255,255,255,0.10)_0%,rgba(255,255,255,0.04)_28%,rgba(255,255,255,0)_56%)]" />

        <div className="absolute right-4 top-4 z-20">
          <BrandButton
            type="button"
            onClick={onClose}
            variant="outline"
            radius="xl"
            size="icon"
            motion="subtle"
            className="relative h-10 w-10 border-white/40 bg-white/90 text-brand-primary shadow-[0_10px_24px_rgba(15,23,42,0.18)] hover:bg-white"
            aria-label="Cerrar modal de predicción"
          >
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="absolute inset-0 m-auto h-5 w-5 text-brand-primary"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 6l12 12" />
              <path d="M18 6l-12 12" />
            </svg>
          </BrandButton>
        </div>

        <div className="relative z-10 flex h-full min-h-0 flex-col overflow-hidden">
          <div className="shrink-0 flex flex-col gap-3 px-4 py-3 sm:px-6 sm:py-6 lg:px-8">
            <div className="flex flex-wrap items-center gap-3">
              <span className="rounded-full border border-black/10 bg-white/55 px-3 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.26em] text-slate-900">
                {getFixtureContextLabel(fixture)}
              </span>
              <span className="text-xs text-slate-900 sm:text-sm">{formatKickoff(fixture.kickoffAt)}</span>
            </div>

            <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] lg:items-center">
              <div className="lg:hidden">
                <p className="text-center font-display text-xl font-semibold leading-tight text-slate-900 sm:text-2xl">
                  {compactFixtureLabel}
                </p>
              </div>

              <div className="hidden min-w-0 lg:block">
                <p className="mt-1 text-lg font-semibold leading-tight text-slate-900 sm:mt-2 sm:text-[1.7rem]">
                  {renderTeamLabel(fixture.teams.home.name)}
                </p>
              </div>

              <div className="rounded-[1.25rem] border border-white/20 bg-white/95 px-3 py-2.5 text-center shadow-[0_18px_40px_rgba(15,23,42,0.18)] sm:rounded-[1.5rem] sm:px-5 sm:py-4">
                <p className="text-[0.55rem] font-semibold uppercase tracking-[0.28em] text-slate-400 sm:text-[0.65rem]">
                  Marcador
                </p>
                <div className="mt-2 flex items-end justify-center gap-2 sm:mt-3 sm:gap-3">
                  <label className="grid gap-2">
                    <span className="sr-only">Goles</span>
                    <input
                      type="number"
                      min="0"
                      max={MAX_PREDICTION_GOALS}
                      step="1"
                      inputMode="numeric"
                      value={draft.homeGoals}
                      onChange={(event) => onChangeHomeGoals(event.target.value)}
                      disabled={!isEditable}
                      className={`${brandInputClass} h-11 w-12 rounded-[1rem] border-brand-primary/10 bg-brand-surface px-2 text-center font-display text-lg tabular-nums shadow-none placeholder:text-slate-300 sm:h-16 sm:w-20 sm:text-2xl`}
                      placeholder="0"
                    />
                  </label>
                  <span className="pb-2 font-display text-xl leading-none text-brand-primary sm:pb-3 sm:text-3xl">
                    -
                  </span>
                  <label className="grid gap-2">
                    <span className="sr-only">Goles</span>
                    <input
                      type="number"
                      min="0"
                      max={MAX_PREDICTION_GOALS}
                      step="1"
                      inputMode="numeric"
                      value={draft.awayGoals}
                      onChange={(event) => onChangeAwayGoals(event.target.value)}
                      disabled={!isEditable}
                      className={`${brandInputClass} h-11 w-12 rounded-[1rem] border-brand-primary/10 bg-brand-surface px-2 text-center font-display text-lg tabular-nums shadow-none placeholder:text-slate-300 sm:h-16 sm:w-20 sm:text-2xl`}
                      placeholder="0"
                    />
                  </label>
                </div>

                {errorMessage ? (
                  <p className="mt-2 text-[0.7rem] font-medium text-rose-700 sm:mt-3 sm:text-sm">
                    {errorMessage}
                  </p>
                ) : null}
              </div>

              <div className="hidden min-w-0 lg:block lg:text-right">
                <p className="mt-1 text-lg font-semibold leading-tight text-slate-900 sm:mt-2 sm:text-[1.7rem] lg:ml-auto">
                  {renderTeamLabel(fixture.teams.away.name)}
                </p>
              </div>
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto px-4 py-2 pb-[7.5rem] sm:px-6 sm:py-5 lg:px-8 lg:pb-[9rem]">
              <div className="grid gap-4 rounded-[1.5rem] border border-slate-200 bg-white p-3.5 sm:p-5">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="mt-1 text-xs text-slate-600 sm:text-sm">
                      ¿Qué dicen los demás javerim?
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 lg:grid-cols-[0.82fr_1.18fr] lg:items-stretch">
                  <section className="flex min-h-[16rem] flex-col rounded-[1.25rem] border border-slate-200 bg-white p-3.5 sm:min-h-[18rem] sm:p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[0.55rem] font-semibold uppercase tracking-[0.22em] text-slate-500 sm:text-[0.6rem]">
                          Quién gana?
                        </p>
                        <p className="mt-1 text-[0.7rem] text-slate-600 sm:text-sm">
                          {leadingOutcomeLabel}
                        </p>
                      </div>
                      <p className="text-sm font-semibold text-slate-500 sm:text-base">
                        {formatPercent(leadingOutcomePercentage)}
                      </p>
                    </div>

                    <div className="mt-3 flex flex-1 flex-col items-center justify-between gap-4 sm:mt-4">
                      <div className="flex justify-center">
                        <div
                          className="relative flex h-24 w-24 items-center justify-center rounded-full sm:h-36 sm:w-36"
                          style={{
                            background: `conic-gradient(${leadingOutcomeColor} ${leadingOutcomePercentage}%, #E6E8ED 0)`,
                          }}
                        >
                          <div className="absolute inset-2 rounded-full bg-slate-50 sm:inset-4" />
                          <div className="relative z-10 text-lg font-semibold text-slate-900 sm:text-2xl">
                            {formatPercent(leadingOutcomePercentage)}
                          </div>
                        </div>
                      </div>

                      <div className="grid w-full gap-2">
                        {communityStats.outcomeBreakdown.length > 0 ? (
                          communityStats.outcomeBreakdown.map((item) => (
                            <div key={item.outcome} className="grid gap-1.5">
                              <div className="flex items-center justify-between gap-3">
                                <div className="flex min-w-0 items-center gap-3">
                                  <span
                                    className="h-2.5 w-2.5 shrink-0 rounded-full border border-slate-200"
                                    style={{ backgroundColor: getOutcomeBadgeColor(item.outcome) }}
                                  />
                                  <span className="truncate text-[0.72rem] font-medium text-slate-700 sm:text-sm">
                                    {getDisplayOutcomeLabel(item.label)}
                                  </span>
                                </div>
                                <span className="text-[0.72rem] font-semibold text-slate-900 sm:text-sm">
                                  {formatPercent(item.percentage)}
                                </span>
                              </div>
                              <div className="h-1.5 rounded-full bg-slate-200">
                                <div
                                  className="h-1.5 rounded-full"
                                  style={{
                                    width: `${Math.max(item.percentage, 4)}%`,
                                    backgroundColor: getOutcomeBadgeColor(item.outcome),
                                  }}
                                />
                              </div>
                            </div>
                          ))
                        ) : (
                          <p className="text-sm text-slate-500">Todavía no hay predicciones del resto.</p>
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="flex min-h-[16rem] flex-col rounded-[1.25rem] border border-slate-200 bg-slate-50 p-3.5 sm:min-h-[18rem] sm:p-4">
                    <div>
                      <p className="text-[0.55rem] font-semibold uppercase tracking-[0.22em] text-slate-500 sm:text-[0.6rem]">
                        Resultados más votados
                      </p>
                      <p className="mt-1 text-[0.72rem] text-slate-600 sm:text-sm">
                        Los marcadores más elegidos por la penca.
                      </p>
                    </div>

                    <div
                      className="mt-3 grid flex-1 grid-cols-1 gap-1.5 overflow-hidden sm:gap-2"
                      style={{
                        gridTemplateColumns: topScorelineCount > 0 ? `repeat(${Math.min(topScorelineCount, 3)}, minmax(0, 1fr))` : "1fr",
                      }}
                    >
                      {communityStats.topScorelines.length > 0 ? (
                        communityStats.topScorelines.map((item) => (
                          <div
                            key={`${item.homeGoals}-${item.awayGoals}`}
                            className="flex h-full items-center justify-center rounded-[0.9rem] border border-white bg-white p-0.5 shadow-sm sm:p-2 sm:[--top-scoreline-card-min-height:var(--top-scoreline-card-min-height-sm)] sm:[--top-scoreline-circle-size:var(--top-scoreline-circle-size-sm)] sm:[--top-scoreline-circle-inset:var(--top-scoreline-circle-inset-sm)] sm:[--top-scoreline-percent-size:var(--top-scoreline-percent-size-sm)] sm:[--top-scoreline-score-size:var(--top-scoreline-score-size-sm)] sm:[--top-scoreline-label-size:var(--top-scoreline-label-size-sm)]"
                            style={{
                              minHeight: "var(--top-scoreline-card-min-height)",
                              ["--top-scoreline-card-min-height"]: topScorelineCardMinHeight,
                              ["--top-scoreline-card-min-height-sm"]: topScorelineCardMinHeightSm,
                              ["--top-scoreline-circle-size"]: topScorelineCircleSize,
                              ["--top-scoreline-circle-size-sm"]: topScorelineCircleSizeSm,
                              ["--top-scoreline-circle-inset"]: topScorelineCircleInset,
                              ["--top-scoreline-circle-inset-sm"]: topScorelineCircleInsetSm,
                              ["--top-scoreline-percent-size"]: topScorelinePercentSize,
                              ["--top-scoreline-percent-size-sm"]: topScorelinePercentSizeSm,
                              ["--top-scoreline-score-size"]: topScorelineScoreSize,
                              ["--top-scoreline-score-size-sm"]: topScorelineScoreSizeSm,
                              ["--top-scoreline-label-size"]: topScorelineLabelSize,
                              ["--top-scoreline-label-size-sm"]: topScorelineLabelSizeSm,
                            }}
                          >
                            <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-center sm:gap-[0.9rem]">
                              <div className="flex items-center justify-center">
                                <div
                                  className="relative flex shrink-0 items-center justify-center rounded-full border border-slate-200"
                                  style={{
                                    width: "var(--top-scoreline-circle-size)",
                                    height: "var(--top-scoreline-circle-size)",
                                    background: `conic-gradient(${getOutcomeBadgeColor(item.outcome)} ${item.percentage}%, #E6E8ED 0)`,
                                  }}
                                >
                                  <div
                                    className="absolute rounded-full bg-white"
                                    style={{
                                      inset: "var(--top-scoreline-circle-inset)",
                                    }}
                                  />
                                  <div
                                    className="relative z-10 font-semibold text-slate-900"
                                    style={{ fontSize: "var(--top-scoreline-percent-size)" }}
                                  >
                                    {formatPercent(item.percentage)}
                                  </div>
                                </div>
                              </div>

                              <div className="min-w-0 pb-0.5">
                                <p
                                  className="font-display leading-none text-slate-900"
                                  style={{ fontSize: "var(--top-scoreline-score-size)" }}
                                >
                                  {renderScorePair(item.homeGoals, item.awayGoals)}
                                </p>
                                <p
                                  className="mt-0.5 max-h-[2rem] overflow-hidden leading-tight text-slate-500 sm:max-h-none"
                                  style={{ fontSize: "var(--top-scoreline-label-size)" }}
                                >
                                  {getOutcomeLabel(item.outcome, fixture.teams.home.name, fixture.teams.away.name)}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="col-span-full flex min-h-[6rem] items-center justify-center rounded-[1rem] border border-dashed border-slate-300 bg-white px-4 py-5 text-center text-sm text-slate-500 sm:min-h-[6.75rem]">
                          Todavía no hay resultados votados.
                        </div>
                      )}
                    </div>
                  </section>
                </div>

              </div>
            </div>

            <div className="shrink-0 flex flex-col gap-3 bg-transparent px-4 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-3 sm:flex-row sm:justify-end sm:px-6 lg:absolute lg:inset-x-0 lg:bottom-0 lg:z-20 lg:px-8 lg:pb-[calc(1.25rem+env(safe-area-inset-bottom))] lg:pt-0">
              <BrandButton
                type="button"
                onClick={onSave}
                disabled={!isEditable || isSaving}
                variant="secondary"
                radius="xl"
                size="md"
                className="w-full sm:w-auto"
              >
                {isSaving ? "Guardando..." : "Guardar"}
              </BrandButton>

              <BrandButton
                type="button"
                onClick={onSaveAndNext}
                disabled={!isEditable || isSaving}
                variant="primary"
                radius="xl"
                size="md"
                motion="subtle"
                className="w-full sm:w-auto"
              >
                {isSaving ? "Guardando..." : "Guardar y ver siguiente"}
              </BrandButton>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function PredictionsPage() {
  const { trackAsync } = useAppLoading();
  useTrackedImagePreload(predictionModalBackground);
  const [tournamentData, setTournamentData] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingFixtureId, setSavingFixtureId] = useState(null);
  const [currentTime, setCurrentTime] = useState(() => Date.now());
  const [activeFixtureId, setActiveFixtureId] = useState(null);
  const [isPendingCollapsed, setIsPendingCollapsed] = useState(false);
  const [isPredictionsCollapsed, setIsPredictionsCollapsed] = useState(false);
  const [isFinishedCollapsed, setIsFinishedCollapsed] = useState(false);

  useEffect(() => {
    let isMounted = true;

    async function loadPredictions() {
      setIsLoading(true);
      setError("");

      try {
        const response = await getPredictions();

        if (isMounted) {
          setTournamentData(response);
          setDrafts(buildDraftMap(response));
        }
      } catch (requestError) {
        if (isMounted) {
          setError(requestError.message || "No pudimos cargar las predicciones.");
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void trackAsync(loadPredictions);

    return () => {
      isMounted = false;
    };
  }, [trackAsync]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setCurrentTime(Date.now());
    }, 10_000);

    return () => {
      window.clearInterval(timer);
    };
  }, []);

  const phases = tournamentData?.phases ?? [];
  const visiblePhases = useMemo(() => getRenderablePhases(phases), [phases]);
  const orderedFixtures = useMemo(
    () => getRenderableFixtures(visiblePhases),
    [visiblePhases],
  );
  const activeFixture = orderedFixtures.find((fixture) => fixture.id === activeFixtureId) ?? null;
  const activeFixtureDraft = activeFixture ? drafts[activeFixture.id] ?? getDraftFixtureValues(activeFixture) : null;
  const activeFixtureDraftError = getPredictionDraftError(activeFixtureDraft);
  const pendingPhases = useMemo(
    () =>
      filterPhasesByFixturePredicate(
        visiblePhases,
        (fixture) => !fixture.prediction,
      ),
    [visiblePhases],
  );
  const predictedPhases = useMemo(
    () => filterPhasesByFixturePredicate(visiblePhases, (fixture) => Boolean(fixture.prediction)),
    [visiblePhases],
  );
  const finishedPhases = useMemo(
    () =>
      filterPhasesByFixturePredicate(
        visiblePhases,
        (fixture) => String(fixture.status?.short ?? "").toUpperCase() === "FINISHED",
      ),
    [visiblePhases],
  );

  useEffect(() => {
    if (pendingPhases.length === 0) {
      setIsPendingCollapsed(false);
    }
  }, [pendingPhases.length]);

  useEffect(() => {
    if (finishedPhases.length === 0) {
      setIsFinishedCollapsed(false);
    }
  }, [finishedPhases.length]);

  async function handleSaveFixture(
    fixture,
    { advanceToNext = false, closeOnSave = true } = {},
  ) {
    const draft = drafts[fixture.id] ?? getDraftFixtureValues(fixture);
    const homeValidationError = validatePredictionGoals(draft.homeGoals, "");
    const awayValidationError = validatePredictionGoals(draft.awayGoals, "");

    if (homeValidationError || awayValidationError) {
      setError(homeValidationError || awayValidationError);
      return;
    }

    setSavingFixtureId(fixture.id);
    setError("");

    try {
      const response = await savePrediction(fixture.id, {
        predictedHomeGoals: Number(draft.homeGoals),
        predictedAwayGoals: Number(draft.awayGoals),
      });

      setTournamentData((current) =>
        updateFixtureInTournament(current, fixture.id, (currentFixture) => ({
          ...currentFixture,
          prediction: {
            ...response.prediction,
            fixtureId: fixture.id,
          },
        })),
      );

      setDrafts((current) => ({
        ...current,
        [fixture.id]: {
          homeGoals: String(response.prediction.predictedHomeGoals),
          awayGoals: String(response.prediction.predictedAwayGoals),
        },
      }));

      if (advanceToNext) {
        const nextFixtureId = getNextFixtureId(orderedFixtures, fixture.id);

        if (nextFixtureId) {
          setActiveFixtureId(nextFixtureId);
        } else {
          setActiveFixtureId(null);
        }
      } else if (closeOnSave) {
        setActiveFixtureId(null);
      }
    } catch (requestError) {
      setError(requestError.message || "No pudimos guardar la predicción.");
    } finally {
      setSavingFixtureId(null);
    }
  }

  function handleOpenFixture(fixture) {
    setActiveFixtureId(fixture.id);
    setError("");
    setDrafts((current) => ({
      ...current,
      [fixture.id]: current[fixture.id] ?? getDraftFixtureValues(fixture),
    }));
  }

  function handleCloseModal() {
    setActiveFixtureId(null);
    setError("");
  }

  return (
    <div className="grid gap-4 overflow-x-hidden">
      <section className="px-1 py-1 sm:px-2 sm:py-2">
        <h2 className="font-display text-[2rem] text-brand-primary sm:text-4xl">
          Predicciones
        </h2>
      </section>

      {isLoading ? (
        <section className="rounded-[2rem] border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          Cargando predicciones...
        </section>
      ) : error ? (
        <section className="rounded-[2rem] border border-rose-200 bg-rose-50 px-4 py-6 text-sm text-rose-700">
          {error}
        </section>
      ) : null}

      {pendingPhases.length > 0 ? (
        <PredictionSection
          title="Partidos por predecir"
          isCollapsed={isPendingCollapsed}
          onToggle={() => setIsPendingCollapsed((current) => !current)}
        >
          <div className="grid gap-6">
            {pendingPhases.map((phase) => (
            <article key={phase.id} className="grid gap-2">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-xl text-brand-primary sm:text-2xl">
                    {phase.displayName}
                  </h3>
                </div>

                <div className="grid gap-2 xl:grid-cols-2 xl:gap-3">
                  {phase.fixtures.map((fixture) =>
                    renderFixtureCard(fixture, {
                      handleOpenFixture,
                      currentTime,
                      renderTeamLabel,
                      renderScorePair,
                    }),
                  )}
                </div>
              </article>
            ))}
          </div>
        </PredictionSection>
      ) : null}

      {predictedPhases.length > 0 ? (
        <PredictionSection
          title="Mis predicciones"
          isCollapsed={isPredictionsCollapsed}
          onToggle={() => setIsPredictionsCollapsed((current) => !current)}
        >
          <div className="grid gap-6">
            {predictedPhases.map((phase) => (
            <article key={phase.id} className="grid gap-2">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-xl text-brand-primary sm:text-2xl">
                    {phase.displayName}
                  </h3>
                </div>

                <div className="grid gap-2 xl:grid-cols-2 xl:gap-3">
                  {phase.fixtures.map((fixture) =>
                    renderFixtureCard(fixture, {
                      handleOpenFixture,
                      currentTime,
                      renderTeamLabel,
                      renderScorePair,
                    }),
                  )}
                </div>
              </article>
            ))}
          </div>
        </PredictionSection>
      ) : !isLoading ? (
        <section className="rounded-[2rem] border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          Todavía no hay partidos listos para mostrar las predicciones.
        </section>
      ) : null}

      {finishedPhases.length > 0 ? (
        <PredictionSection
          title="Partidos finalizados"
          isCollapsed={isFinishedCollapsed}
          onToggle={() => setIsFinishedCollapsed((current) => !current)}
        >
          <div className="grid gap-6">
            {finishedPhases.map((phase) => (
              <article key={phase.id} className="grid gap-2">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-display text-xl text-brand-primary sm:text-2xl">
                    {phase.displayName}
                  </h3>
                </div>

                <div className="grid gap-2 xl:grid-cols-2 xl:gap-3">
                  {phase.fixtures.map((fixture) =>
                    renderFixtureCard(fixture, {
                      handleOpenFixture,
                      currentTime,
                      renderTeamLabel,
                      renderScorePair,
                    }),
                  )}
                </div>
              </article>
            ))}
          </div>
        </PredictionSection>
      ) : !isLoading ? (
        <section className="rounded-[2rem] border border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
          Todavía no hay partidos finalizados para mostrar.
        </section>
      ) : null}

      {activeFixture ? (
        <PredictionModal
          fixture={activeFixture}
          draft={activeFixtureDraft ?? getDraftFixtureValues(activeFixture)}
          isEditable={getFixtureEditability(activeFixture, currentTime)}
          isSaving={savingFixtureId === activeFixture.id}
          errorMessage={activeFixtureDraftError || error}
          onClose={handleCloseModal}
          onChangeHomeGoals={(value) => {
            setDrafts((current) => ({
              ...current,
              [activeFixture.id]: {
                ...(current[activeFixture.id] ?? getDraftFixtureValues(activeFixture)),
                homeGoals: value,
              },
            }));
          }}
          onChangeAwayGoals={(value) => {
            setDrafts((current) => ({
              ...current,
              [activeFixture.id]: {
                ...(current[activeFixture.id] ?? getDraftFixtureValues(activeFixture)),
                awayGoals: value,
              },
            }));
          }}
          onSave={() => {
            void handleSaveFixture(activeFixture, {
              advanceToNext: false,
              closeOnSave: true,
            });
          }}
          onSaveAndNext={() => {
            void handleSaveFixture(activeFixture, {
              advanceToNext: true,
              closeOnSave: false,
            });
          }}
        />
      ) : null}
    </div>
  );
}
